import { useEffect, useState } from "react";
import { LayoutList } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MAIN_MODULES } from "@/lib/modules";
import { DEPARTMENTS, PRESETS, TERMS } from "@/lib/workspace";
import { Panel, SectionLabel } from "@/components/common/ui-kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";

const LABEL = Object.fromEntries(MAIN_MODULES.map((m) => [m.id, m.label]));

const PresetPicker = ({ value, onPick }) => (
  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="workspace-presets">
    {PRESETS.map((p) => (
      <button key={p.id} type="button" onClick={() => onPick(p)} data-testid={`workspace-preset-${p.id}`}
        className={cn("rounded-card border p-3 text-left transition-colors hover:border-brand-300",
          value === p.id ? "border-brand-500 bg-brand-50" : "border-line bg-white")}>
        <span className="block text-sm font-semibold text-ink">{p.label}</span>
        <span className="mt-1 block text-xs text-slate-500">{p.description}</span>
      </button>
    ))}
  </div>
);

const DeptEditor = ({ d, f, setLabel, toggle }) => {
  const off = f.hidden_departments.includes(d.id);
  return (
    <div className="rounded-card border border-line p-3" data-testid={`workspace-dept-${d.id}`}>
      <div className="flex items-center gap-3">
        <Switch checked={!off} onCheckedChange={() => toggle(d.id)} data-testid={`workspace-dept-switch-${d.id}`} />
        <Input value={f.labels[`dept:${d.id}`] || ""} placeholder={d.label} maxLength={40} className="h-8 font-semibold"
          onChange={(e) => setLabel(`dept:${d.id}`, e.target.value)} data-testid={`workspace-label-dept-${d.id}`} />
      </div>
      {!off && (
        <div className="mt-3 grid gap-2 pl-12">
          {d.modules.map((id) => (
            <div key={id} className="grid gap-2 sm:grid-cols-2">
              <Input value={f.labels[id] || ""} placeholder={LABEL[id]} maxLength={40} className="h-8 text-sm"
                onChange={(e) => setLabel(id, e.target.value)} data-testid={`workspace-label-${id}`} />
              {TERMS[id] && (
                <Input value={f.labels[`${id}:one`] || ""} placeholder={`Singolare: ${TERMS[id][0]}`} maxLength={40} className="h-8 text-sm"
                  onChange={(e) => setLabel(`${id}:one`, e.target.value)} data-testid={`workspace-label-one-${id}`} />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const WorkspacePanel = () => {
  const { me } = useAuth();
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/workspace").then((r) => setF(r.data)).catch(() => {}); }, []);
  if (!f || me?.role?.key !== "super_admin" || me?.is_demo_user) return null;
  const setLabel = (k, v) => setF({ ...f, labels: { ...f.labels, [k]: v } });
  const toggle = (id) => setF({ ...f, hidden_departments: f.hidden_departments.includes(id) ? f.hidden_departments.filter((x) => x !== id) : [...f.hidden_departments, id] });
  const pick = (p) => setF({ sector: p.id, hidden_departments: [...p.hidden], labels: { ...p.labels } });
  const save = async () => {
    setBusy(true);
    try { await api.put("/admin/workspace", f); toast.success("Menu aggiornato"); setTimeout(() => window.location.reload(), 600); }
    catch (x) { toast.error(errorMessage(x)); } finally { setBusy(false); }
  };
  return (
    <Panel className="p-5 lg:col-span-3" data-testid="workspace-panel" data-user-content="">
      <div className="mb-2 flex items-center gap-2"><LayoutList className="h-4 w-4 text-slate-400" /><SectionLabel>Settore, reparti e nomi</SectionLabel></div>
      <p className="mb-4 text-xs text-slate-500">Scegli un modello per il tuo settore, poi accendi o spegni i reparti e rinomina le voci. Lascia un campo vuoto per il nome standard. I nomi valgono anche per pulsanti e testi delle pagine: indica il singolare (es. «lavoro») per frasi come «Nuovo lavoro». Vale per tutti gli utenti della tua azienda.</p>
      <PresetPicker value={f.sector} onPick={pick} />
      <div className="mt-5 grid gap-3 lg:grid-cols-2">
        {DEPARTMENTS.map((d) => <DeptEditor key={d.id} d={d} f={f} setLabel={setLabel} toggle={toggle} />)}
      </div>
      <div className="mt-4 flex justify-end">
        <Button onClick={save} disabled={busy} data-testid="workspace-save">{busy ? "Salvataggio…" : "Salva"}</Button>
      </div>
    </Panel>
  );
};
